package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.db.TranslationEngineData
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{GenericState, GenericStateGenerator, ServerDataAttribute, StateAttribute}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.ReactElement

case class EngineSelectionState(
                                 serverEnginesState: ServerDataAttribute[List[TranslationEngineData]],
                                 activeEngineIdentifiersState: StateAttribute[List[String]],
                               ) extends GenericState {

  import EngineSelectionState.VALID_ENGINES

  def isValidEngine(translationEngineData: TranslationEngineData) = VALID_ENGINES.contains(translationEngineData.identifier)

  def allServerEngines = serverEnginesState.value
  def setAllServerEngines(serverEngines: List[TranslationEngineData]) = serverEnginesState.setData(serverEngines)

  def activeEngines = activeEngineIdentifiersState.value
  def setActiveEngines(serverEngines: List[String]) = setState(activeEngineIdentifiersState, serverEngines)

  def addActiveEngine(engineIdentifier: String): Unit = {
    setActiveEngines(activeEngines.filterNot(_ == engineIdentifier) :+ engineIdentifier)
  }

  def removeActiveEngine(engineIdentifier: String) = {
    setActiveEngines(activeEngines.filterNot(_ == engineIdentifier))
  }

  def awaitingServerEngines = serverEnginesState.loading

  def isSelected(engineIdentifier: String) = activeEngines.contains(engineIdentifier)

  def loadServerEngines() = {
    TranslationServer.getAllEngines(handleServerEngines)
  }

  def handleServerEngines = ServerResponseUtil.handleObjectResultResponse[Seq[TranslationEngineData]](
    handleObject = data => setAllServerEngines(data.filter(isValidEngine(_)).toList),
    onAnyError = serverEnginesState.setError(_)

  )
}

object EngineSelectionState {
  val VALID_ENGINES = List("deepl", "google")
}

object EngineSelectionStateGenerator extends GenericStateGenerator[EngineSelectionState] {

  override def generateEmptyDefaultState(): EngineSelectionState = EngineSelectionState(
    serverEnginesState = emptyServerDataState[List[TranslationEngineData]](),
    activeEngineIdentifiersState = emptyDefaultState(List[String]())
  )
}

@react
object EngineSelectionStateProvider {

  case class Props(children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val serverEnginesState = ServerDataAttribute(useState[Either[Option[String], List[TranslationEngineData]]](Left(None)))
    val activeEngineIdentifiersState = StateAttribute(useState(List[String]("deepl", "google")))

    val engineSelectionState = EngineSelectionState(
      serverEnginesState,
      activeEngineIdentifiersState
    )

    useEffect(() => {
      engineSelectionState.loadServerEngines()
    }, Vector())

    EngineSelectionStateGenerator.Provider(engineSelectionState)(
      props.children:_*
    )
  }
}
